import {StyleSheet} from "aphrodite";
import React, {memo, useCallback} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalDialog, ModalPanel} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {type ToolPersona} from "@khan/khanmigo-tools";
import {InitialRequestUrl} from "@khan/shared-core";

import {
    renderInfoToast,
    renderToast,
} from "#notifications-package/toast-notification.tsx";

import {strings} from "./constants.ts";
import {createViewDocumentHref} from "./utils.ts";

import linkSimple from "@phosphor-icons/core/regular/link-simple.svg";

interface GetShareLinkModalProps {
    title: string;
    closeModal: () => void;
    documentID: string;
    onCopyLinkClick: () => void;
    persona: ToolPersona;
}

interface ErrorToastProps {
    handleCopyLinkClick: () => void;
}

const ErrorToast: React.FC<ErrorToastProps> = memo(({handleCopyLinkClick}) => (
    <span>
        {strings().linkCopiedError}{" "}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error */}
        <span
            onClick={handleCopyLinkClick}
            style={{
                textDecoration: "underline",
                cursor: "pointer",
            }}
        >
            {strings().clickThisToTryAgain}
        </span>
    </span>
));

const GetShareLinkModal: React.FC<GetShareLinkModalProps> = ({
    title,
    closeModal,
    documentID,
    onCopyLinkClick,
    persona,
}) => {
    const handleCopyLinkClick = useCallback(() => {
        closeModal();

        onCopyLinkClick();
        // Create view-only link and copy to clipboard
        const href = createViewDocumentHref(documentID, persona);
        window.navigator.clipboard
            .writeText(`${InitialRequestUrl.host}${href}`)
            .then(() => {
                renderInfoToast(strings().linkCopied);
            })
            .catch(() => {
                renderToast(
                    ({ErrorNotification}) => (
                        <ErrorNotification
                            content={
                                <ErrorToast
                                    handleCopyLinkClick={handleCopyLinkClick}
                                />
                            }
                        />
                    ),
                    {position: "bottom center"},
                );
            });
    }, [closeModal, documentID, onCopyLinkClick, persona]);

    return (
        <ModalDialog
            style={styles.modalContainer}
            aria-labelledby="get-share-link-modal"
        >
            <ModalPanel
                closeButtonVisible={true}
                testId="get-share-link-modal"
                content={
                    <View>
                        <LabelLarge>
                            {strings().share} "{title}"
                        </LabelLarge>
                        <Strut size={spacing.xSmall_8} />
                        <LabelSmall>{strings().shareLinkModal}</LabelSmall>
                        <Strut size={spacing.large_24} />
                        <View style={styles.copyButtonContainer}>
                            <Button
                                onClick={handleCopyLinkClick}
                                kind="secondary"
                                startIcon={linkSimple}
                            >
                                {strings().copyLink}
                            </Button>
                        </View>
                    </View>
                }
            />
        </ModalDialog>
    );
};

const styles = StyleSheet.create({
    modalContainer: {
        height: "fit-content",
        maxWidth: 400,
    },
    copyButtonContainer: {
        justifyContent: "flex-end",
        flexDirection: "row",
    },
});

export default GetShareLinkModal;
